import React from 'react';
import { 
  Card, 
  Badge, 
  Button, 
  ButtonGroup, 
  Dropdown, 
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { Link } from 'react-router-dom';

const BannerCard = ({ 
  banner, 
  onToggleActive, 
  onDuplicate, 
  onDelete, 
  loading 
}) => {
  if (!banner) return null;

  const {
    id,
    title,
    subtitle,
    image,
    button_text,
    button_link,
    is_active,
    is_visible,
    view_count = 0,
    click_count = 0,
    clickThroughRate = 0,
    sort_order,
    formattedCreatedAt,
    formattedStartDate,
    formattedEndDate,
    statusVariant,
    statusText,
    created_by_name
  } = banner;

  const renderActionButton = (icon, tooltip, variant, onClick, disabled = false) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{tooltip}</Tooltip>}
    >
      <Button
        variant={variant}
        size="sm"
        onClick={onClick}
        disabled={disabled || loading}
      >
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <i className={icon} />
        )}
      </Button>
    </OverlayTrigger>
  );

  return (
    <Card className="banner-card border-0 shadow-sm h-100">
      {/* Banner Image */}
      <div className="banner-image-container position-relative">
        {image ? (
          <Card.Img
            variant="top"
            src={image}
            alt={title}
            className="banner-image"
            style={{
              height: '200px',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div 
            className="banner-placeholder d-flex align-items-center justify-content-center bg-light"
            style={{ height: '200px' }}
          >
            <i className="las la-image text-muted" style={{ fontSize: '3rem' }} />
          </div>
        )}
        
        {/* Status Badge */}
        <Badge 
          bg={statusVariant} 
          className="position-absolute top-0 start-0 m-2"
        >
          {statusText}
        </Badge>

        {/* Sort Order Badge */}
        <Badge 
          bg="dark" 
          className="position-absolute top-0 end-0 m-2"
        >
          #{sort_order}
        </Badge>
      </div>

      <Card.Body className="d-flex flex-column">
        {/* Title and Subtitle */}
        <div className="flex-grow-1">
          <h6 className="card-title mb-2 fw-bold text-truncate" title={title}>
            {title}
          </h6>
          
          {subtitle && (
            <p className="card-text text-muted small mb-2" style={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {subtitle}
            </p>
          )}

          {/* Button Info */}
          {button_text && (
            <div className="mb-2">
              <small className="text-muted">
                <i className="las la-external-link-alt me-1" />
                {button_text}
              </small>
            </div>
          )}
        </div>

        {/* Metrics */}
        <div className="banner-metrics mb-3">
          <div className="row g-2 text-center">
            <div className="col-4">
              <div className="metric-item">
                <div className="metric-value text-info fw-bold">
                  {view_count.toLocaleString('pt-BR')}
                </div>
                <div className="metric-label small text-muted">
                  <i className="las la-eye me-1" />
                  Views
                </div>
              </div>
            </div>
            <div className="col-4">
              <div className="metric-item">
                <div className="metric-value text-warning fw-bold">
                  {click_count.toLocaleString('pt-BR')}
                </div>
                <div className="metric-label small text-muted">
                  <i className="las la-mouse-pointer me-1" />
                  Clicks
                </div>
              </div>
            </div>
            <div className="col-4">
              <div className="metric-item">
                <div className="metric-value text-success fw-bold">
                  {clickThroughRate}%
                </div>
                <div className="metric-label small text-muted">
                  <i className="las la-chart-line me-1" />
                  CTR
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="card-footer-info small text-muted mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <i className="las la-user me-1" />
              {created_by_name || 'Admin'}
            </span>
            <span>
              <i className="las la-calendar me-1" />
              {formattedCreatedAt}
            </span>
          </div>
          
          {(formattedStartDate || formattedEndDate) && (
            <div className="mt-1">
              <i className="las la-clock me-1" />
              {formattedStartDate && (
                <span>Início: {formattedStartDate}</span>
              )}
              {formattedStartDate && formattedEndDate && ' • '}
              {formattedEndDate && (
                <span>Fim: {formattedEndDate}</span>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          {/* Edit Button */}
          <Button
            as={Link}
            to={`/admin/marketplace/banners/edit/${id}`}
            variant="outline-primary"
            size="sm"
            className="flex-grow-1"
            disabled={loading}
          >
            <i className="las la-edit me-1" />
            Editar
          </Button>

          {/* More Actions Dropdown */}
          <Dropdown as={ButtonGroup}>
            <Button
              variant={is_active ? "outline-warning" : "outline-success"}
              size="sm"
              onClick={() => onToggleActive(id, is_active)}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className={`las la-${is_active ? 'pause' : 'play'}`} />
              )}
            </Button>

            <Dropdown.Toggle
              split
              variant="outline-secondary"
              size="sm"
              disabled={loading}
            />

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => onDuplicate(id)}>
                <i className="las la-copy me-2" />
                Duplicar
              </Dropdown.Item>
              
              {button_link && (
                <>
                  <Dropdown.Item 
                    href={button_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <i className="las la-external-link-alt me-2" />
                    Testar Link
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
              
              <Dropdown.Item 
                onClick={() => onDelete(id)}
                className="text-danger"
              >
                <i className="las la-trash me-2" />
                Excluir
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BannerCard;